import tokenService from '../utils/tokenService';

type MissingTokenStrategy = 'throw' | 'skip' | 'allow-empty';
type TokenResolver = () => Promise<string | null>;

interface PostToNetlifyFunctionOptions {
  functionName: string;
  payload?: unknown;
  tokenHeaderName?: string;
  missingTokenStrategy?: MissingTokenStrategy;
}

const DEFAULT_TOKEN_ERROR = 'Session invalide: token manquant.';

const getTokenService = () => {
  const moduleValue = require('../utils/tokenService') as {
    default?: { getToken?: () => Promise<string | null> };
    getToken?: () => Promise<string | null>;
  };

  return (tokenService as { getToken?: () => Promise<string | null> } | undefined)
    || moduleValue.default
    || moduleValue;
};

export const buildNetlifyFunctionUrl = (functionName: string): string => {
  return `/.netlify/functions/${functionName}`;
};

const buildNetlifyHeaders = (
  tokenHeaderName: string,
  token: string
): Record<string, string> => ({
  'Content-Type': 'application/json',
  [tokenHeaderName]: token,
});

const buildNetlifyRequestInit = (
  payload: unknown,
  tokenHeaderName: string,
  token: string
): RequestInit => ({
  method: 'POST',
  headers: buildNetlifyHeaders(tokenHeaderName, token),
  body: JSON.stringify(payload),
});

const resolveClientToken = async (
  getToken: TokenResolver,
  missingTokenStrategy: MissingTokenStrategy
): Promise<string | null> => {
  const token = await getToken();

  if (token) {
    return token;
  }

  if (missingTokenStrategy === 'throw') {
    throw new Error(DEFAULT_TOKEN_ERROR);
  }

  if (missingTokenStrategy === 'allow-empty') {
    return '';
  }

  return null;
};

export const parseNetlifyResponseBody = async (response: Response): Promise<any> => {
  const rawBody = await response.text();

  if (!rawBody) {
    return null;
  }

  try {
    return JSON.parse(rawBody);
  } catch {
    return { message: rawBody };
  }
};

export const readNetlifyErrorMessage = async (
  response: Response,
  fallbackMessage: string
): Promise<string> => {
  const parsedBody = await parseNetlifyResponseBody(response);

  if (typeof parsedBody === 'string' && parsedBody.trim()) {
    return parsedBody;
  }

  if (parsedBody?.detail) {
    return parsedBody.detail;
  }

  if (parsedBody?.message) {
    return parsedBody.message;
  }

  return fallbackMessage;
};

export const createNetlifyClient = (getToken: TokenResolver) => ({
  buildNetlifyFunctionUrl,
  postToNetlifyFunction: async ({
    functionName,
    payload,
    tokenHeaderName = 'X-Client-Token',
    missingTokenStrategy = 'throw',
  }: PostToNetlifyFunctionOptions): Promise<Response | null> => {
    const token = await resolveClientToken(getToken, missingTokenStrategy);

    if (token === null) {
      return null;
    }

    return fetch(
      buildNetlifyFunctionUrl(functionName),
      buildNetlifyRequestInit(payload, tokenHeaderName, token)
    );
  },
  parseNetlifyResponseBody,
});

const defaultNetlifyClient = createNetlifyClient(async () => {
  const tokenGetter = getTokenService()?.getToken;
  return tokenGetter ? tokenGetter() : null;
});

export const postToNetlifyFunction = defaultNetlifyClient.postToNetlifyFunction;
